import { useState } from 'react';
import { useQuery } from 'react-query';
/** State Managers */
import { useSelector } from 'react-redux';
import { useAuthTenant, useAuthUser } from 'store/auth';
/** Components */
import { DefaultTable } from 'Components/DefaultTable';
import { Container } from '../../Components/Container';
import { Information } from 'Components/Information';
import { tableHeader } from './table-header.js';
import { ModalConfirmDelete } from 'Components/ModalConfirmDelete';
import { PageLoaderList } from 'Components/PageLoader/List';
import { NoData } from 'Components/NoData';
import { Button, FormControl, InputLabel, MenuItem, Pagination, Select, Stack } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { Card } from 'Components/Card';
import { Column, Row } from 'Components/Grid';
import { CustomTextField } from 'Components/Form/styles';
/** Services */
import { deleteUserVerifyCode, getUsersVerifyCodesPaged } from 'services/users-service';
/** Third Library Modal */
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
/** Icons */
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { useTranslation } from 'react-i18next';

export const UsersVerifyCodes = () => {
  const { t } = useTranslation(['user', 'common']);
  const user = useSelector(useAuthUser);
  const selectedTenant = useSelector(useAuthTenant);
  const SwalReact = withReactContent(Swal);
  const [open, setOpen] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [page, setPage] = useState(1);

  const { control, register, reset, watch } = useForm({
    defaultValues: {
      companyKey: selectedTenant.slug,
      searchContent: '',
    },
  });

  const getContentSearch = () => {
    if (!watch('searchContent')) return '';

    return watch('searchContent');
  };

  const {
    isLoading,
    error,
    data: pagedUsersVerifyCodes,
  } = useQuery(['getUsersVerifyCodes', refetch, page], async () => {
    if (selectedTenant.slug === undefined) return [];
    const search = getContentSearch();

    const result = await getUsersVerifyCodesPaged(watch('companyKey'), page, 15, search);
    if (result?.data.data?.length > 0) {
      return result?.data;
    }
    return {
      data: [],
      page,
      totalPages: 0,
    };
  });

  const onDelete = item => {
    setItemToDelete(item);
    setOpen(true);
  };

  const onClose = () => {
    setItemToDelete(null);
    setOpen(false);
  };

  const onConfirm = async () => {
    try {
      const result = await deleteUserVerifyCode(itemToDelete?._id);
      if (result.status === 204) {
        onClose();
        setRefetch(!refetch);
        SwalReact.fire({
          title: t('user:alert.successTitle'),
          text: t('user:verifyCodes.success.deactivated'),
          icon: 'success',
        });
      } else {
        throw new Error('Ocorreu um erro ao tentar desativar o código de verificação do Usuário!');
      }
    } catch (error) {
      SwalReact.fire({
        title: 'Erro!',
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  return (
    <Container
      title={t('user:verifyCodes.title')}
      breadcrumb={t('user:verifyCodes.breadcrumb')}
      showButtonAdd
      linkPage="/users-verify-codes"
    >
      <Information
        title={t('user:verifyCodes.infoTitle')}
        description={t('user:verifyCodes.description')}
        style={{
          marginTop: '24px',
        }}
      />
      <Card className="mb-2 mt-3">
        <Row>
          <Column xs={12} md={3}>
            <Controller
              control={control}
              name="companyKey"
              render={({ field: { onChange, value } }) => (
                <FormControl fullWidth sx={{ margin: '0 auto' }}>
                  <InputLabel id="company-label" shrink>
                    {t('user:verifyCodes.filter.company')}
                  </InputLabel>
                  <Select
                    displayEmpty
                    labelId="company-label"
                    id="companyFilter"
                    notched
                    label={t('common:labels.filter')}
                    value={value}
                    onChange={onChange}
                    size="small"
                    style={{ marginBottom: '16px !important' }}
                    sx={{
                      '& > .MuiSelect-select.MuiSelect-outlined': {
                        paddingLeft: '0 !important',
                      },
                    }}
                  >
                    <MenuItem value={''} key={-1}>
                      {t('user:verifyCodes.filter.selectCompany')}
                    </MenuItem>
                    {user.companiesWithAccess?.map((item, index) => (
                      <MenuItem value={item.slug} key={`company-filter-${index}`}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Column>
          <Column xs={12} md={4}>
            <CustomTextField
              {...register('searchContent')}
              label={t('user:verifyCodes.filter.search')}
              sx={{
                minHeight: '40px',
                maxHeight: '40px',
                margin: '0 auto',
                width: '100%',
                ' > div': {
                  minHeight: '40px',
                },
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Column>
          <Column xs={12} md={'auto'}>
            <Button
              size="small"
              variant="outlined"
              sx={{
                width: 'fit-content!important',
                borderRadius: '12px',
                padding: '10px 24px 10px 24px',
                textTransform: 'none',
              }}
              onClick={() => {
                reset();
                setRefetch(!refetch);
              }}
            >
              {t('common:buttons.clear')}
            </Button>
          </Column>
          <Column xs={12} md={'auto'}>
            <Button
              size="small"
              variant="contained"
              sx={{
                width: 'fit-content!important',
                borderRadius: '12px',
                padding: '10px 24px 10px 24px',
                textTransform: 'none',
              }}
              endIcon={<SearchOutlinedIcon />}
              disabled={watch('typeFilter') === ''}
              onClick={() => {
                setRefetch(!refetch);
              }}
            >
              {t('common:buttons.filter')}
            </Button>
          </Column>
        </Row>
      </Card>
      {isLoading && <PageLoaderList></PageLoaderList>}
      {error && <div>{t('user:verifyCodes.error')}</div>}
      {!isLoading && !error && pagedUsersVerifyCodes.data?.length === 0 && (
        <NoData description={t('user:verifyCodes.noData')} />
      )}
      {!isLoading && !error && pagedUsersVerifyCodes.data?.length > 0 && (
        <div className="container">
          <DefaultTable
            rows={tableHeader(t)}
            cells={pagedUsersVerifyCodes.data}
            onClickDelete={onDelete}
            link="users-verify-codes"
            hideEdit
            hideShow
          />
          <Stack sx={{ justifyContent: 'center', flexDirection: 'row', marginTop: '16px' }}>
            <Pagination
              count={pagedUsersVerifyCodes.totalPages}
              onChange={(_, selectedPage) => {
                setPage(selectedPage);
              }}
              color="primary"
              page={page}
              showFirstButton
              showLastButton
            />
          </Stack>
          <ModalConfirmDelete
            description={t('user:verifyCodes.delete.confirm', { code: itemToDelete?.code, username: itemToDelete?.username })}
            open={open}
            onClose={onClose}
            onConfirm={onConfirm}
          />
        </div>
      )}
    </Container>
  );
};
